import { useCallback, useState, useRef, memo } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import CheckIcon from '@mui/icons-material/Check';
import CopyIcon from '@mui/icons-material/ContentCopy';
import { Tooltip } from '@trinity/chat-components/components/presentation/Tooltip';
import { useTheme } from '@trinity/chat-components/components/Theme';
import { BaseMessageButton } from './Message.Button';

type Props = {
  className?: string;
  onClick: () => unknown;
  color?: string;
};

export const CopyToClipboard = memo(({ color, className, onClick }: Props) => {
  const [showCopied, setShowCopied] = useState<boolean>(false);
  const timer = useRef<NodeJS.Timeout>(undefined);

  const theme = useTheme();

  const handleClick = useCallback(() => {
    setShowCopied(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setShowCopied(false), 2000);
    onClick();
  }, [onClick]);

  return (
    <Tooltip
      title={showCopied ? `Copied to clipboard` : `Copy to clipboard`}
      enterDelay={1000}
      enterNextDelay={500}
    >
      <Root className={className} onClick={showCopied ? undefined : handleClick}>
        {showCopied ? (
          <StyledCheckIcon />
        ) : (
          <StyledCopyIcon
            css={css({
              color: color ?? theme.palette.badge.citation.color,
            })}
          />
        )}
      </Root>
    </Tooltip>
  );
});

export const CopyButton = memo(({ className, onClick }: Props) => {
  const [showCopied, setShowCopied] = useState<boolean>(false);
  const timer = useRef<NodeJS.Timeout>(undefined);

  const handleClick = useCallback(() => {
    setShowCopied(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setShowCopied(false), 2000);
    onClick();
  }, [onClick]);

  return (
    <BaseMessageButton
      className={className}
      onClick={showCopied ? undefined : handleClick}
      css={compactContainerCss}
    >
      {showCopied ? <StyledCheckIcon /> : <StyledCopyIcon />}

      <ButtonCopy>Copy</ButtonCopy>
    </BaseMessageButton>
  );
});

const compactContainerCss = css({
  '@container message-actions (max-width: 450px)': {
    minWidth: 'auto',
  },
});

const ButtonCopy = styled.div({
  marginLeft: 5,
  '@container message-actions (max-width: 450px)': {
    display: 'inline-block',
    maxWidth: 0,
    overflow: 'hidden',
    opacity: 0,
    whiteSpace: 'nowrap',
    marginLeft: 0,
    transition: 'max-width 0.2s ease, opacity 0.2s ease, margin-left 0.2s ease',
    'button:hover &': {
      maxWidth: 100,
      opacity: 1,
      marginLeft: 5,
    },
  },
});

const Root = styled.div`
  cursor: pointer;
  transition: 0.3s;
`;

const StyledCheckIcon = styled(CheckIcon)(
  ({ theme }) => `
  color: ${theme.palette.green.main};
  font-size: 18px !important;
  opacity: 0.5;
`
);

const StyledCopyIcon = styled(CopyIcon)(
  ({ theme }) => `
  font-size: 18px !important;
`
);
